'use client';

import PropTypes from 'prop-types';
import SanityImage from '@sanity-image';
import styles from './exhibition-card.module.scss';
import MarkdownParser from 'components/rich-text/markdown-parser';
import RedundantLink from 'components/accesibility/redundant-link';
import ExhibitionDisplayDates from 'components/exhibitions/display-dates';
import GTMLink from 'components/controls/links/GTMLink';
import analyticsPropType from 'lib/propTypes/analyticsPropType';
import I8nBadge from 'components/audio-player/I18n-badge';
import DisplayGalleryAndFloor from 'components/exhibitions/display-gallery-and-floor';
import { routeForDocument } from '@metmuseum/routes';
import useAnimateIntoView from 'hooks/use-animate-into-view';
import cn from 'classnames';
import hoverStyles from '../hover-state.module.scss';

const ExhibitionCard = (props) => {
	const {
		exhibition,
		analytics,
		cardWidth = '600px',
		cardMaxWidth = 'var(--max-width-unclamped)',
		languages,
		showGalleries = false,
		url,
		imageSizes = `(max-width: ${cardWidth}) 90vw, ${cardWidth}`,
	} = props;

	const {
		ref: animateRef,
		className: animateClassName,
		style: animateStyle,
	} = useAnimateIntoView();
	const link =
		url ||
		(exhibition.externalUrl
			? exhibition.externalUrl
			: routeForDocument({
					_type: 'exhibition',
					slug: exhibition.slug,
				}));

	return (
		<article
			ref={animateRef}
			className={cn(styles.exhibitionCard, animateClassName, hoverStyles.card)}
			style={{
				...animateStyle,
				'--exhibition-card-width': cardWidth,
				'--exhibition-card-max-width': cardMaxWidth,
			}}
		>
			<figure className={styles.figure}>
				<RedundantLink
					href={link}
					Content_Type={exhibition._type}
					Content_Name={exhibition.title}
					Content_Block={analytics?.content_block}
					event={analytics?.event}
				/>
				<SanityImage fill={true} image={exhibition.image} sizes={imageSizes} />
			</figure>
			<div className={styles.info}>
				{exhibition.status && (
					<div className={styles.status}>{exhibition.status}</div>
				)}
				<div className={styles.title} role="heading" aria-level={2}>
					<GTMLink
						event={analytics?.event}
						Content_Type={exhibition._type}
						Content_Name={exhibition.title}
						Content_Block={analytics?.content_block}
						className={styles.link}
						href={link}
					>
						<MarkdownParser>{exhibition.title}</MarkdownParser>
					</GTMLink>
				</div>
				<div className={styles.meta}>
					<div>
						<div>
							{!showGalleries ? (
								<ExhibitionDisplayDates exhibitionDates={exhibition.dates} />
							) : (
								<DisplayGalleryAndFloor galleries={exhibition.galleries} />
							)}
						</div>
						{!!languages?.length && <I8nBadge languages={languages} />}
					</div>
				</div>
			</div>
		</article>
	);
};

ExhibitionCard.propTypes = {
	exhibition: PropTypes.shape({
		_type: PropTypes.any,
		externalUrl: PropTypes.string,
		image: PropTypes.any,
		dates: ExhibitionDisplayDates.propTypes.exhibitionDates,
		buildings: PropTypes.array,
		slug: PropTypes.string,
		status: PropTypes.string,
		title: PropTypes.string,
		galleries: PropTypes.array,
	}),
	analytics: analyticsPropType,
	cardWidth: PropTypes.string,
	cardMaxWidth: PropTypes.string,
	languages: PropTypes.arrayOf(PropTypes.string), // TODO: when exhibitions become i18n'ed, move this to the exhibition item
	showGalleries: PropTypes.bool,
	url: PropTypes.string,
};

export default ExhibitionCard;
